import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Home, Sparkles } from 'lucide-react';

const LINKS = [
  { label: 'Services', href: '#services' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Areas', href: '#areas' },
  { label: 'Contact', href: '#quote' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-obsidian/85 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-[76px] flex items-center justify-between gap-4">
        <a href="#top" data-testid="nav-logo" className="group flex items-center gap-3 shrink-0">
          <img
            src="/logo.png"
            alt="GMP Solution"
            className="h-14 lg:h-16 rounded-xl bg-white px-3 py-2 object-contain transition-transform duration-300 group-hover:scale-[1.03] shadow-[0_10px_35px_rgba(0,0,0,0.35)] ring-1 ring-white/20"
          />
          <span className="flex flex-col relative pl-4">
            <span className="absolute left-0 top-1 bottom-1 w-1 rounded-full bg-mint" />
            <span className="font-display font-extrabold text-white text-base sm:text-xl lg:text-[1.8rem] tracking-tight leading-none whitespace-nowrap drop-shadow-[0_2px_16px_rgba(95,168,204,0.55)]">
              GMP Solution
            </span>
            <span className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.2em] text-[#8FC6E2] font-medium mt-2 whitespace-nowrap">
              Residential &amp; Commercial Cleaning
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-5">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-[13px] font-medium text-stone-200 hover:text-white transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <a
            href="tel:+16473900855"
            data-testid="nav-phone-badge"
            className="hidden 2xl:flex items-center gap-2 text-[13px] font-mono text-stone-200 hover:text-white transition-colors whitespace-nowrap"
          >
            <Phone size={14} className="text-mint-vibrant" />
            +1 (647) 390-0855
          </a>
          <a
            href="#quote"
            data-testid="nav-cta-button"
            className="bg-mint hover:bg-mint-deep text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-95 whitespace-nowrap"
          >
            Get a Free Quote
          </a>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2 shrink-0"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-obsidian/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-white text-lg font-medium py-1"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+16473900855"
                className="flex items-center gap-2 text-stone-200 text-sm font-mono py-1"
              >
                <Phone size={14} className="text-mint-vibrant" /> +1 (647) 390-0855
              </a>
              <a
                href="#quote"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-cta"
                className="mt-1 bg-mint text-white text-center text-sm font-semibold px-5 py-3 rounded-full"
              >
                Get a Free Quote
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
